import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { AuthShell } from "@/pages/SetPassword";

export default function NotMe() {
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/auth/not-me?t=${encodeURIComponent(t)}`).then((r) => { setInfo(r.data); setDone(r.data.actioned); }).catch((e) => setErr(errMsg(e)));
  }, [t]);
  const [mailFailed, setMailFailed] = useState(false);
  const secure = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/not-me", { t });
      setMailFailed(!!data.email_failed);
      setDone(true);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Secure your account" subtitle="Use this if you didn't sign in to Aspire yourself.">
      {err && <div data-testid="not-me-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {!info && !err && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
      {info && done && (
        <div data-testid="not-me-done" className="flex gap-2 rounded-md bg-emerald-50 px-3 py-3 text-sm text-emerald-800"><ShieldCheck className="h-4 w-4 shrink-0" />Your account is secured. You've been signed out everywhere and your old password no longer works. Check your email for a link to set a new password. Your administrators have been told.</div>
      )}
      {done && mailFailed && (
        <div data-testid="not-me-mail-failed" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">We couldn't send the reset email just now. Use <Link to="/forgot-password" className="font-medium underline">Forgot password</Link> in a few minutes, or ask your administrator to reset it.</div>
      )}
      {info && !done && (
        <div className="space-y-4" data-testid="not-me-panel">
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <div className="mb-1 flex items-center gap-2 font-semibold"><ShieldAlert className="h-4 w-4" />Sign-in being reported</div>
            <div data-testid="not-me-details">{new Date(info.at).toLocaleString()} · {info.device} · {info.country || "Unknown location"} (IP {info.ip})</div>
          </div>
          <p className="text-sm text-slate-600">Securing your account signs you out on every device, blocks your current password and emails you a link to choose a new one. System Administrators and PMO will be alerted.</p>
          <Button data-testid="not-me-secure-btn" className="w-full bg-red-600 hover:bg-red-700" disabled={busy} onClick={secure}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}This wasn't me — secure my account</Button>
        </div>
      )}
      <Link to="/login" className="block text-sm font-medium text-indigo-600 hover:underline" data-testid="not-me-login-link">Go to sign in</Link>
    </AuthShell>
  );
}
